/** Segmented control — SPEC-2 §6. */

import { cn } from "@/lib/cn";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Shown under the control when this option is selected. */
  hint?: string;
}

/**
 * One choice out of three-to-five, all visible at once.
 *
 * Used for the outcome of a method application, where a dropdown would be
 * actively worse: `partial` and `failed` say something about the *method*,
 * `abandoned` says the work stopped for unrelated reasons, and a person can
 * only make that distinction if they can see all the options while deciding.
 * Hiding them behind a tap turns a judgement into a default.
 *
 * DS `Segmented`: a pill track on `surface-2`; the selected option is a
 * `surface` pill with a hairline, the others are `text-muted`. No yellow.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label?: string;
  className?: string;
}) {
  const selected = options.find((o) => o.value === value);
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && <span className="text-body text-p-text-muted">{label}</span>}
      <div
        role="radiogroup"
        aria-label={label}
        className="flex gap-1 rounded-full border border-p-border bg-p-surface-2 p-1"
      >
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(option.value)}
              className={cn(
                "press-surface inline-flex min-h-[36px] flex-1 items-center justify-center rounded-full px-2 text-center text-meta font-semibold leading-4 transition-colors",
                active ? "bg-p-surface text-p-text shadow-[0_0_0_1px_var(--p-border)]" : "text-p-text-muted",
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {selected?.hint && <p className="text-meta text-p-text-muted">{selected.hint}</p>}
    </div>
  );
}
